<template>
  <v-container fluid class="dash mt-0 pt-2">
    <!-- tool order dialog -->
    <v-dialog v-model="orderDialog" max-width="800">
      <v-card class="grey lighten-3" v-if="dialogOrder">
        <v-card-title primary-title class="justify-center primary py-4 mb-2">
          <v-btn
            class="mr-4"
            color="white"
            small
            :to="`/jobs/orders/${dialogOrder.jobSite.id}`"
            ><v-icon dark left>receipt</v-icon>Orders</v-btn
          >
          <v-btn
            class="mr-4"
            color="white"
            small
            :to="`/jobs/cost/${dialogOrder.jobSite.id}`"
            ><v-icon dark left>local_atm</v-icon>Cost</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn color="white" outlined @click="orderDialog = false" small
            ><v-icon dark left>close</v-icon>Close</v-btn
          >
        </v-card-title>
        <v-card-text class="px-2">
          <my-tool-order
            v-if="orderDialog"
            :toolOrderNumber="dialogOrder.orderNumber"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- the day: who is working where -->
    <section class="dash-hero">
      <div class="dash-dateline">
        <v-btn
          icon
          small
          @click="goToPrevDay"
          aria-label="Previous day"
          class="dash-noprint"
          ><v-icon>chevron_left</v-icon></v-btn
        >
        <v-dialog
          ref="dialog"
          v-model="datePickerDialog"
          :return-value.sync="workersOnSiteDateFormatted"
          persistent
          width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              class="dash-date"
              v-bind="attrs"
              v-on="on"
              aria-label="Pick a date"
            >
              {{ dayLabel }}
            </button>
          </template>
          <v-date-picker
            v-model="workersOnSiteDateFormatted"
            :max="todayKey"
            scrollable
            color="primary"
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="datePickerDialog = false">
              Cancel
            </v-btn>
            <v-btn
              text
              color="primary"
              @click="goToDate(workersOnSiteDateFormatted)"
            >
              Go to date
            </v-btn>
          </v-date-picker>
        </v-dialog>
        <v-btn
          icon
          small
          @click="goToNextDay"
          :disabled="isToday"
          aria-label="Next day"
          class="dash-noprint"
          ><v-icon>chevron_right</v-icon></v-btn
        >
        <v-btn
          v-if="!isToday"
          text
          small
          color="primary"
          class="ml-1 dash-noprint"
          @click="goToToday"
          >Back to today</v-btn
        >
        <v-spacer></v-spacer>
        <div class="dash-noprint">
          <v-btn
            text
            small
            class="dash-quiet-btn"
            :loading="loadingWorkersData"
            @click="getWorkers(workersOnSiteDate)"
            ><v-icon left small>refresh</v-icon>Refresh</v-btn
          >
          <v-btn
            text
            small
            class="dash-quiet-btn"
            :disabled="!workersOnSite.length"
            @click="downloadCSV"
            ><v-icon left small>download</v-icon>CSV</v-btn
          >
        </div>
      </div>

      <v-skeleton-loader
        v-if="loadingWorkers"
        type="heading"
        class="mt-3"
        max-width="520"
      ></v-skeleton-loader>
      <template v-else>
        <h1 class="dash-headline">{{ headline }}</h1>
        <p class="dash-subline" v-if="reportsExpected">
          Daily reports filed for {{ reportsFiled }} of
          {{ plural(reportsExpected, "site") }}.
          <span v-if="isToday && reportsFiled < reportsExpected"
            >Reports are due by the end of the day.</span
          >
        </p>
      </template>
    </section>

    <!-- job sites on the selected day -->
    <v-card outlined class="dash-panel dash-print">
      <div class="dash-panel__head">
        <div>
          <h2 class="dash-title">Job sites</h2>
          <p class="dash-note">
            Everyone who punched in, by job. Man days count through this day.
          </p>
        </div>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          class="dash-search dash-noprint"
          dense
          outlined
          hide-details
          clearable
          prepend-inner-icon="search"
          label="Filter by job, address or name"
        ></v-text-field>
      </div>
      <v-skeleton-loader
        v-if="loadingWorkers"
        type="table-tbody"
        class="px-4"
      ></v-skeleton-loader>
      <v-data-table
        v-else
        class="dash-table dash-table--sites"
        :headers="siteHeaders"
        :items="workersOnSite"
        :search="search"
        :items-per-page="100"
        :loading="loadingWorkersData"
        item-key="jobSite.id"
        hide-default-footer
        mobile-breakpoint="760"
      >
        <template v-slot:no-data>
          <p class="dash-empty">
            {{
              isToday
                ? "No one has punched in yet today."
                : `No one punched in on ${dayLabel}.`
            }}
          </p>
        </template>
        <template v-slot:item.count="{ item }">
          <span class="dash-figure">{{ item.count }}</span>
        </template>
        <template v-slot:item.jobSite.jobId="{ item }">
          <a
            :href="`/jobs/cost/${item.jobSite.id}`"
            target="_blank"
            class="dash-job"
            >{{ item.jobSite.jobId }}</a
          >
          <div class="dash-sub">{{ item.jobSite.address }}</div>
        </template>
        <template v-slot:item.crew="{ item }">
          <span class="dash-sub dash-sub--wrap">{{ item.crew }}</span>
        </template>
        <template v-slot:item.foreman="{ item }">
          <span
            :class="{ 'dash-sub': !item.foremanAssigned }"
            :title="
              item.foremanAssigned
                ? 'Assigned to the job'
                : 'No foreman assigned; showing who punched in'
            "
            >{{ item.foreman }}</span
          >
        </template>
        <template v-slot:item.manDays="{ item }">
          <div v-if="item.internal" class="dash-sub">Internal</div>
          <div
            v-else-if="item.manDays !== null || manDaysObj[item.jobSite.id]"
            class="dash-mandays"
          >
            <man-day-gauge
              v-if="item.jobSite.manDays > 0"
              :percent="item.manDaysPercent"
              class="mb-1"
            />
            <span class="dash-num">{{
              numberWithCommas(
                item.manDays !== null
                  ? item.manDays
                  : manDaysObj[item.jobSite.id],
              )
            }}</span>
            <span class="dash-sub" v-if="item.jobSite.manDays > 0">
              of {{ numberWithCommas(item.jobSite.manDays) }}</span
            >
            <span class="dash-sub" v-else> no budget set</span>
          </div>
          <!-- only for a job with no jobStats doc yet -->
          <v-btn
            v-else
            x-small
            text
            color="primary"
            :loading="item.loadingManDays"
            @click="
              getManDays(item.jobSite.id);
              item.loadingManDays = true;
            "
            >Count</v-btn
          >
        </template>
        <template v-slot:item.reportStatus="{ item }">
          <a
            v-if="item.reportStatus === 'filed'"
            class="dash-status dash-status--ok"
            target="_blank"
            :href="
              `/daily-reports/${item.jobSite.id}/${workersOnSiteDateFormatted}/summary`
            "
            ><v-icon small color="#2e7d32">check_circle</v-icon> Filed</a
          >
          <span
            v-else-if="item.reportStatus === 'missing'"
            class="dash-status dash-status--bad"
            ><v-icon small color="#c62828">error</v-icon> Missing</span
          >
          <span v-else-if="item.reportStatus === 'pending'" class="dash-sub"
            >Not yet</span
          >
          <span v-else class="dash-sub">Not required</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- man-day budgets -->
    <v-card outlined class="dash-panel dash-noprint">
      <div class="dash-panel__head">
        <div>
          <h2 class="dash-title">Man-day budgets</h2>
          <p class="dash-note">
            Open jobs with a Man Days budget, most used first. Updated daily at
            6am.
          </p>
        </div>
        <v-spacer></v-spacer>
        <v-switch
          v-model="manDayActiveOnly"
          dense
          hide-details
          inset
          class="mt-0 pt-0"
          label="Punched in within 30 days"
        ></v-switch>
      </div>
      <div class="dash-filters">
        <v-chip-group v-model="budgetFilter" mandatory>
          <v-chip
            v-for="filter in budgetFilters"
            :key="filter.value"
            :value="filter.value"
            small
            outlined
            filter
            :class="`dash-filter dash-filter--${filter.value}`"
          >
            {{ filter.label }}
            <span class="dash-filter__count">{{ filter.count }}</span>
          </v-chip>
        </v-chip-group>
      </div>
      <v-data-table
        class="dash-table"
        :headers="budgetHeaders"
        :items="budgetRows"
        :loading="loadingManDays"
        item-key="id"
        sort-by="percent"
        sort-desc
        :items-per-page="100"
        hide-default-footer
        mobile-breakpoint="760"
      >
        <template v-slot:no-data>
          <p class="dash-empty">No jobs in this group.</p>
        </template>
        <template v-slot:item.jobId="{ item }">
          <a :href="`/jobs/cost/${item.id}`" target="_blank" class="dash-job">{{
            item.jobId
          }}</a>
          <div class="dash-sub">
            {{ item.jobSite.street }}, {{ item.jobSite.city }}
          </div>
        </template>
        <template v-slot:item.percent="{ item }">
          <div class="dash-gauge-cell">
            <man-day-gauge :percent="item.percent" />
            <span class="dash-num" :class="`dash-tone--${item.tone}`"
              >{{ item.percent }}%</span
            >
          </div>
        </template>
        <template v-slot:item.used="{ item }">
          <span class="dash-num">{{ numberWithCommas(item.used) }}</span>
          <span class="dash-sub"> of {{ numberWithCommas(item.budget) }}</span>
        </template>
        <template v-slot:item.perWeek="{ item }">
          <span class="dash-num">{{ item.perWeek || "None" }}</span>
        </template>
        <template v-slot:item.finishSort="{ item }">
          <span :class="{ 'dash-sub': !item.finishIsDate }">{{
            item.finishLabel
          }}</span>
        </template>
        <template v-slot:item.lastPunchDate="{ item }">
          {{ item.lastPunchDate | moment("MMM D, YYYY") }}
        </template>
      </v-data-table>
    </v-card>

    <!-- yard activity -->
    <v-card outlined class="dash-panel dash-noprint">
      <div class="dash-feeds">
        <div v-for="feed in feeds" :key="feed.key" class="dash-feed">
          <div class="dash-feed__head">
            <h2 class="dash-title dash-title--small">{{ feed.title }}</h2>
            <span class="dash-live" title="Updates as orders change"
              ><span class="dash-live__dot"></span>Live</span
            >
          </div>
          <p v-if="!feed.items.length" class="dash-empty dash-empty--feed">
            {{ feed.empty }}
          </p>
          <v-list dense class="py-0">
            <!-- a v-list-item without a click listener renders inert -->
            <v-list-item
              v-for="item in feed.expanded ? feed.items : feed.items.slice(0, 6)"
              :key="item.id"
              class="dash-feed__row"
              v-on="feed.clickable ? { click: () => rowClick(item) } : {}"
            >
              <div class="dash-feed__main">
                <div class="dash-feed__street">{{ item.jobSite.street }}</div>
                <div class="dash-sub">
                  {{ item.jobSite.jobId }}, {{ item.jobSite.city }}
                </div>
              </div>
              <div class="dash-feed__side">
                <div class="dash-sub">
                  {{
                    feed.key === "demobs"
                      ? formatDate(item.orderDate)
                      : item.orderedBy && item.orderedBy.firstName
                  }}
                </div>
                <span
                  class="dash-pill"
                  :class="`dash-pill--${feedStatus(feed.key, item).tone}`"
                  >{{ feedStatus(feed.key, item).label }}</span
                >
              </div>
            </v-list-item>
          </v-list>
          <v-btn
            v-if="feed.items.length > 6"
            text
            small
            color="primary"
            class="mt-1"
            @click="toggleFeed(feed.key)"
            >{{
              feed.expanded ? "Show fewer" : `Show all ${feed.items.length}`
            }}</v-btn
          >
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import fb from "@/main";
import { uniqBy, groupBy, orderBy, sum, uniq, chunk } from "lodash";
import { mapGetters, mapState } from "vuex";
import MyToolOrder from "../../views/MyToolOrder";
import ManDayGauge from "./ManDayGauge";
import moment from "moment";

const toneFor = (percent) => {
  if (percent >= 100) return "over";
  if (percent >= 75) return "watch";
  return "track";
};

export default {
  components: {
    MyToolOrder,
    ManDayGauge,
  },
  data: () => ({
    loadingWorkersData: false,
    loadingWorkers: true,
    datePickerDialog: false,
    workersOnSiteDate: moment(),
    workersOnSiteDateFormatted: moment().format("YYYY-MM-DD"),
    search: "",
    manDaysObj: {},
    workersOnSite: [],
    dayWorkerCount: 0,
    siteHeaders: [
      { text: "Workers", value: "count", width: 90 },
      { text: "Job", value: "jobSite.jobId", width: 280 },
      { text: "Crew", value: "crew", sortable: false, width: 240 },
      { text: "Foreman", value: "foreman" },
      { text: "OM", value: "om" },
      { text: "Man days", value: "manDays", width: 180 },
      { text: "Daily report", value: "reportStatus", width: 120 },
      { text: "", value: "searchText", align: " d-none" },
    ],
    // open jobs with a Man Days budget, and jobStats docs by job id (shared
    // with the job sites table)
    manDayJobs: [],
    manDayStats: {},
    loadingManDays: true,
    manDayActiveOnly: true,
    budgetFilter: "all",
    budgetHeaders: [
      { text: "Job", value: "jobId", width: 300 },
      { text: "OM", value: "om" },
      { text: "Used", value: "percent", width: 240 },
      { text: "Man days", value: "used", width: 150 },
      { text: "Per week", value: "perWeek", align: "right", width: 100 },
      { text: "Reaches budget", value: "finishSort", width: 150 },
      { text: "Last punch", value: "lastPunchDate", width: 130 },
    ],
    demobs: [],
    toolOrders: [],
    expandedFeeds: [],
    isPM: false,
    orderDialog: false,
    dialogOrder: null,
  }),
  computed: {
    ...mapGetters([
      "toolOrdersIncomplete",
      "toolOrdersForPm",
      "toolOrdersIncompleteForPm",
    ]),
    ...mapState({
      user: "user",
    }),
    todayKey() {
      return moment().format("YYYY-MM-DD");
    },
    isToday() {
      return this.workersOnSiteDate.isSame(moment(), "day");
    },
    dayLabel() {
      const sameYear = this.workersOnSiteDate.isSame(moment(), "year");
      return this.workersOnSiteDate.format(
        sameYear ? "dddd, MMMM D" : "dddd, MMMM D, YYYY",
      );
    },
    headline() {
      if (!this.workersOnSite.length) {
        return this.isToday
          ? "No one has punched in yet"
          : "No one punched in this day";
      }
      return `${this.plural(this.dayWorkerCount, "worker")} on ${this.plural(
        this.workersOnSite.length,
        "site",
      )}`;
    },
    reportsExpected() {
      return this.workersOnSite.filter((w) => !w.internal).length;
    },
    reportsFiled() {
      return this.workersOnSite.filter((w) => w.reportStatus === "filed")
        .length;
    },
    // Same pace and projection as the man-day alert email (manDayStatsJob).
    manDayBudgetJobs() {
      const today = moment().format("YYYY-MM-DD");
      const paceStart = moment()
        .subtract(28, "days")
        .format("YYYY-MM-DD");
      const activeSince = moment()
        .subtract(30, "days")
        .format("YYYY-MM-DD");
      return this.manDayJobs
        .filter((job) => this.manDayStats[job.id])
        .map((job) => {
          const stats = this.manDayStats[job.id];
          const days = stats.days || {};
          const used = stats.totalManDays || 0;
          const perDay =
            sum(
              Object.keys(days)
                .filter((day) => day >= paceStart && day < today)
                .map((day) => days[day].manDays),
            ) / 28;
          const remaining = job.manDays - used;
          let finishLabel = "No recent punches";
          let finishSort = "9999";
          let finishIsDate = false;
          if (remaining <= 0) {
            finishLabel = "Reached";
            finishSort = "0000";
          } else if (perDay) {
            const finish = moment().add(Math.ceil(remaining / perDay), "days");
            finishSort = finish.format("YYYY-MM-DD");
            finishIsDate = finish.diff(moment(), "days") <= 365;
            finishLabel = finishIsDate
              ? finish.format("MMM D, YYYY")
              : "Over a year";
          }
          const percent = Math.round((used / job.manDays) * 100);
          return {
            id: job.id,
            jobSite: job,
            jobId: job.jobId,
            om: (job.projectManagers || [])
              .map((pm) => this.fixName(pm.name))
              .join(", "),
            budget: job.manDays,
            used,
            percent,
            tone: toneFor(percent),
            perWeek: Math.round(perDay * 7),
            finishLabel,
            finishSort,
            finishIsDate,
            lastPunchDate: stats.lastPunchDate || "",
          };
        })
        .filter(
          (job) => !this.manDayActiveOnly || job.lastPunchDate >= activeSince,
        );
    },
    budgetFilters() {
      const count = (tone) =>
        this.manDayBudgetJobs.filter((job) => job.tone === tone).length;
      return [
        { value: "all", label: "All", count: this.manDayBudgetJobs.length },
        { value: "over", label: "Over budget", count: count("over") },
        { value: "watch", label: "75% and up", count: count("watch") },
        { value: "track", label: "Under 75%", count: count("track") },
      ];
    },
    budgetRows() {
      if (this.budgetFilter === "all") return this.manDayBudgetJobs;
      return this.manDayBudgetJobs.filter(
        (job) => job.tone === this.budgetFilter,
      );
    },
    feeds() {
      const newestFirst = (list) =>
        orderBy(
          list || [],
          (o) => (o.orderDate && o.orderDate.seconds) || 0,
          "desc",
        );
      return [
        {
          key: "orders",
          title: this.isPM ? "My tool orders" : "Latest tool orders",
          empty: "No tool orders yet.",
          clickable: true,
          items: newestFirst(this.isPM ? this.toolOrdersForPm : this.toolOrders),
        },
        {
          key: "incomplete",
          title: this.isPM ? "My incomplete orders" : "Incomplete tool orders",
          empty: "Every fulfilled order is complete.",
          clickable: true,
          items: newestFirst(
            this.isPM
              ? this.toolOrdersIncompleteForPm
              : this.toolOrdersIncomplete,
          ),
        },
        {
          key: "demobs",
          title: "Demobilizations",
          empty: "No recent demobilizations.",
          clickable: false,
          items: newestFirst(this.demobs),
        },
      ].map((feed) => ({
        ...feed,
        expanded: this.expandedFeeds.includes(feed.key),
      }));
    },
  },
  created() {
    // Employees and job sites looked up by the job sites table, kept for the
    // life of the page so moving between days only fetches ones not seen yet.
    this.employeeCache = {};
    this.jobSiteCache = {};
    this.unsubscribers = [];
    if (this.user.role == "Project Manager" && !this.user.isAdmin)
      this.isPM = true;
    // The incomplete feed reads the store's open tool orders. Check the loaded
    // list rather than the incomplete getter, which is empty whenever nothing
    // is incomplete and would start another listener on every visit.
    if (!this.$store.state.toolOrders.length) {
      this.$store.dispatch("getToolOrders", fb);
    }

    this.getWorkers();
    this.getManDayStats();

    // latest tool orders
    this.unsubscribers.push(
      fb.toolOrders_DB
        .orderBy("orderDate", "desc")
        .limit(20)
        .onSnapshot((querySnapshot) => {
          let toolOrders = [];
          querySnapshot.forEach(function(doc) {
            toolOrders.push({
              id: doc.id,
              ...doc.data(),
            });
          });
          this.toolOrders = uniqBy(
            toolOrders.filter((to) => !to.cancelled),
            "jobSite.id",
          );
        }),
    );

    // demobs
    this.unsubscribers.push(
      fb.toolPickups_DB
        .where("pickupType", "==", "Demobilization")
        .orderBy("orderDate", "desc")
        .limit(15)
        .onSnapshot((querySnapshot) => {
          let demobs = [];
          querySnapshot.forEach(function(doc) {
            demobs.push({
              id: doc.id,
              ...doc.data(),
            });
          });
          this.demobs = demobs;
        }),
    );
  },
  beforeDestroy() {
    this.unsubscribers.forEach((unsubscribe) => unsubscribe());
  },
  methods: {
    async getManDayStats() {
      try {
        const jobsRes = await fb.db
          .collection("jobSites")
          .where("manDays", ">", 0)
          .get();
        const jobs = [];
        jobsRes.forEach((doc) => {
          if (doc.data().status === "open") {
            jobs.push({ id: doc.id, ...doc.data() });
          }
        });
        const statsDocs = await Promise.all(
          jobs.map((job) =>
            fb.db
              .collection("jobStats")
              .doc(job.id)
              .get(),
          ),
        );
        statsDocs.forEach((doc) => {
          if (doc.exists) this.$set(this.manDayStats, doc.id, doc.data());
        });
        this.manDayJobs = jobs;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log("getManDayStats error", err);
      }
      this.loadingManDays = false;
    },
    // Fetches docs by id, 10 per query (the SDK's limit for "in"), into cache.
    // Ids that turn out not to exist are cached as null so they aren't retried.
    async fetchDocsById(collection, ids, cache) {
      const missing = uniq(ids).filter((id) => id && !(id in cache));
      const results = await Promise.all(
        chunk(missing, 10).map((ids) =>
          fb.db
            .collection(collection)
            .where(fb.firebase.firestore.FieldPath.documentId(), "in", ids)
            .get(),
        ),
      );
      missing.forEach((id) => (cache[id] = null));
      results.forEach((res) =>
        res.forEach((doc) => (cache[doc.id] = { id: doc.id, ...doc.data() })),
      );
    },
    async getWorkers(date = moment()) {
      this.loadingWorkersData = true;
      this.workersOnSiteDate = date;
      const day = date.format("YYYY-MM-DD");
      this.workersOnSiteDateFormatted = day;
      const [dailyReportsRes, workersRes] = await Promise.all([
        fb.db
          .collection("dailyReports")
          .where("complete", "==", true)
          .where("date", "==", day)
          .get(),
        fb.db
          .collection("punchRecords")
          .where(
            "time",
            ">",
            date
              .clone()
              .startOf("day")
              .toDate(),
          )
          .where(
            "time",
            "<",
            date
              .clone()
              .endOf("day")
              .toDate(),
          )
          .where("direction", "==", "in")
          .get(),
      ]);
      // A newer request (the user moved to another day) has taken over.
      if (date !== this.workersOnSiteDate) return;
      const dailyReports = [];
      dailyReportsRes.forEach((dr) => {
        dailyReports.push(dr.data());
      });
      let punchRecords = [];
      workersRes.forEach(function(doc) {
        punchRecords.push({
          ...doc.data(),
        });
      });

      // Only the employees, job sites and job stats this day needs.
      const jobSiteIds = uniq(punchRecords.map((pr) => pr.jobSite));
      await Promise.all([
        this.fetchDocsById(
          "employees",
          punchRecords.map((pr) => pr.employee),
          this.employeeCache,
        ),
        this.fetchDocsById("jobSites", jobSiteIds, this.jobSiteCache),
        this.getJobStats(jobSiteIds),
      ]);
      if (date !== this.workersOnSiteDate) return;

      punchRecords = punchRecords.map((pr) => {
        return { ...this.employeeCache[pr.employee], ...pr };
      });
      const isPast = day < moment().format("YYYY-MM-DD");
      const punchRecordsGrouped = groupBy(punchRecords, "jobSite");
      const workers = [];
      Object.keys(punchRecordsGrouped).forEach((k) => {
        const jobSite = this.jobSiteCache[k];
        if (!jobSite) return;
        const punches = punchRecordsGrouped[k];
        const count = uniqBy(punches, "employee").length;
        const departments = groupBy(uniqBy(punches, "employee"), "department");
        const crew = orderBy(
          Object.keys(departments).map((dept) => ({
            dept: dept === "undefined" ? "no department" : dept,
            n: departments[dept].length,
          })),
          "n",
          "desc",
        )
          .map((d) => `${d.n} ${d.dept}`)
          .join(", ");
        const assigned = (jobSite.assignedForeman || []).map((a) =>
          this.fixName(a.name),
        );
        const onSite = [
          ...(departments.foreman || []),
          ...(departments.supervisor || []),
        ].map((f) => this.fixName(`${f.firstName} ${f.lastName}`));
        const foreman = (assigned.length ? assigned : onSite).join(", ");
        const om = (jobSite.projectManagers || [])
          .map((pm) => this.fixName(pm.name))
          .join(", ");
        const internal = (jobSite.jobId || "").startsWith("15");
        const dailyReport = dailyReports.find((dr) => dr.jobSite.id === k);
        let reportStatus = "pending";
        if (internal) reportStatus = "internal";
        else if (dailyReport) reportStatus = "filed";
        else if (isPast) reportStatus = "missing";
        const manDays = internal ? null : this.manDaysThrough(k, day, count);
        workers.push({
          jobSite,
          count,
          crew,
          departments,
          foreman,
          foremanAssigned: assigned.length > 0,
          om,
          internal,
          dailyReport,
          reportStatus,
          manDays,
          manDaysPercent:
            manDays !== null && jobSite.manDays > 0
              ? Math.round((manDays / jobSite.manDays) * 100)
              : 0,
          loadingManDays: false,
          searchText: [jobSite.address, crew, foreman, om].join(" "),
        });
      });
      this.workersOnSite = orderBy(workers, "count", "desc");
      this.dayWorkerCount = uniq(
        punchRecords
          .filter((pr) => this.jobSiteCache[pr.jobSite])
          .map((pr) => pr.employee),
      ).length;

      this.loadingWorkers = false;
      this.loadingWorkersData = false;
    },
    // jobStats docs for these jobs, into manDayStats, skipping ones loaded.
    async getJobStats(jobSiteIds) {
      const missing = jobSiteIds.filter((id) => !(id in this.manDayStats));
      const statsDocs = await Promise.all(
        missing.map((id) =>
          fb.db
            .collection("jobStats")
            .doc(id)
            .get(),
        ),
      );
      statsDocs.forEach((doc) =>
        this.$set(this.manDayStats, doc.id, doc.exists ? doc.data() : null),
      );
    },
    // Man days through the end of `day`: cached days before it, plus the
    // workers counted live for that day. Null when the job has no jobStats
    // doc yet, so the Count button shows instead.
    manDaysThrough(jobSiteId, day, count) {
      const stats = this.manDayStats[jobSiteId];
      if (!stats) return null;
      const days = stats.days || {};
      return (
        sum(
          Object.keys(days)
            .filter((d) => d < day)
            .map((d) => days[d].manDays),
        ) + count
      );
    },
    async getManDays(jobSiteId) {
      const manDays = await this.$store.dispatch("manDaysCount", {
        fb: fb,
        jobId: jobSiteId,
      });
      this.$set(this.manDaysObj, jobSiteId, manDays);
    },
    goToNextDay() {
      this.getWorkers(this.workersOnSiteDate.clone().add(1, "d"));
    },
    goToToday() {
      this.getWorkers();
    },
    goToDate(date) {
      this.getWorkers(moment(date));
      this.datePickerDialog = false;
    },
    goToPrevDay() {
      this.getWorkers(this.workersOnSiteDate.clone().subtract(1, "d"));
    },
    toggleFeed(key) {
      this.expandedFeeds = this.expandedFeeds.includes(key)
        ? this.expandedFeeds.filter((k) => k !== key)
        : [...this.expandedFeeds, key];
    },
    feedStatus(key, item) {
      if (key === "demobs") {
        return item.status === "open"
          ? { label: "Open", tone: "watch" }
          : { label: this.capitalize(item.status || "Closed"), tone: "quiet" };
      }
      if (key === "incomplete") return { label: "Incomplete", tone: "over" };
      return item.fulfilled
        ? { label: "Fulfilled", tone: "quiet" }
        : { label: "Unfulfilled", tone: "watch" };
    },
    formatDate(timestamp) {
      if (!timestamp || !timestamp.toDate) return "";
      return moment(timestamp.toDate()).format("MMM D");
    },
    capitalize(s) {
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    plural(n, word) {
      return `${this.numberWithCommas(n)} ${word}${n === 1 ? "" : "s"}`;
    },
    rowClick(value) {
      this.dialogOrder = value;
      this.orderDialog = true;
    },
    numberWithCommas(x) {
      if (!x) return 0;
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    // Names on jobSites are denormalized copies taken when the PM/foreman was
    // assigned, so old records carry whatever casing was typed at signup
    // ("andrew horan", "Wilson CHIMBORAZO"). Tidy them for display only.
    fixName(n) {
      return (n || "")
        .replace(/\s+/g, " ")
        .trim()
        .split(" ")
        .map((w) => {
          // Leave deliberately mixed-case names alone (McKenzie, DeLuca).
          if (w !== w.toLowerCase() && w !== w.toUpperCase()) return w;
          // Capitalize each part so O'NEILL and paul-andres come out right.
          return w.replace(
            /[^'’-]+/g,
            (p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase(),
          );
        })
        .join(" ");
    },
    downloadCSV() {
      const headers = [
        "Workers",
        "Job ID",
        "Address",
        "Worker Type",
        "Assigned Foreman",
        "OM",
        "Man Days",
        "Daily",
      ];

      const csvData = this.workersOnSite.map((item) => {
        // Format Worker Type as "2 foreman / 31 demolition / 2 burner / 1 operator"
        const workerType = Object.keys(item.departments)
          .map((dept) => `${item.departments[dept].length} ${dept}`)
          .join(" / ");

        return [
          item.count,
          item.jobSite.jobId,
          item.jobSite.address,
          workerType,
          item.foreman,
          item.om,
          item.manDays !== null ? item.manDays : "",
          item.dailyReport ? "yes" : "no",
        ];
      });

      // Create CSV content
      const csvContent = [headers, ...csvData]
        .map((row) => row.map((field) => `"${field}"`).join(","))
        .join("\n");

      // Create and download file
      const filename = `workers-on-site-${this.workersOnSiteDate.format(
        "YYYY-MM-DD",
      )}.csv`;
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
  },
};
</script>

<style scoped>
.dash {
  --ink: #1c2534;
  --muted: #5d6878;
  --line: #dde3ea;
  --cobalt: #15489e;
  --hivis: #e8650f;
  --hivis-text: #a84a08;
  --over: #c62828;
  --ok: #2e7d32;
  --display: "Barlow Semi Condensed", "Barlow", Roboto, sans-serif;
  font-family: "Barlow", Roboto, sans-serif;
  color: var(--ink);
  max-width: 1480px;
}

/* the day */
.dash-hero {
  padding: 8px 4px 20px;
}
.dash-dateline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  margin-left: -6px;
}
.dash-date {
  font-family: var(--display);
  font-size: 17px;
  font-weight: 600;
  color: var(--cobalt);
  padding: 4px 8px;
  border-radius: 6px;
}
.dash-date:hover {
  background: rgba(21, 72, 158, 0.08);
}
.dash-date:focus-visible {
  outline: 2px solid var(--cobalt);
  outline-offset: 2px;
}
.dash-headline {
  font-family: var(--display);
  font-size: 44px;
  line-height: 1.1;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 6px 0 4px;
  font-variant-numeric: tabular-nums;
}
.dash-subline {
  font-size: 16px;
  color: var(--muted);
  margin: 0;
  max-width: 70ch;
}
.dash ::v-deep .v-btn {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
}
.dash-quiet-btn {
  color: var(--muted) !important;
}

/* panels */
.dash-panel {
  border-color: var(--line) !important;
  border-radius: 10px !important;
  margin-bottom: 24px;
  overflow: hidden;
}
.dash-panel__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 20px;
  padding: 18px 20px 12px;
}
.dash-title {
  font-family: var(--display);
  font-size: 22px;
  font-weight: 600;
  line-height: 1.2;
  margin: 0;
}
.dash-title--small {
  font-size: 18px;
}
.dash-note {
  font-size: 14px;
  color: var(--muted);
  margin: 2px 0 0;
}
.dash-search {
  max-width: 320px;
}
.dash-filters {
  padding: 0 14px 4px;
}
.dash-filter__count {
  margin-left: 6px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.dash-filter--over .dash-filter__count {
  color: var(--over);
}
.dash-filter--watch .dash-filter__count {
  color: var(--hivis-text);
}

/* tables */
.dash-table ::v-deep th {
  font-size: 13px !important;
  font-weight: 500;
  color: var(--muted) !important;
  white-space: nowrap;
  border-bottom-color: var(--line) !important;
}
.dash-table ::v-deep th:first-child,
.dash-table ::v-deep td:first-child {
  padding-left: 20px !important;
}
.dash-table ::v-deep td {
  font-size: 14px !important;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
  height: auto !important;
  vertical-align: top;
  border-bottom-color: var(--line) !important;
}
.dash-figure {
  font-family: var(--display);
  font-size: 24px;
  font-weight: 600;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.dash-job {
  font-family: var(--display);
  font-size: 16px;
  font-weight: 600;
  color: var(--cobalt);
  text-decoration: none;
}
.dash-job:hover {
  text-decoration: underline;
}
.dash-sub {
  font-size: 13px;
  color: var(--muted);
  line-height: 1.4;
}
.dash-sub--wrap {
  display: inline-block;
  max-width: 34ch;
}
.dash-num {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.dash-mandays {
  min-width: 130px;
}
.dash-gauge-cell {
  display: grid;
  grid-template-columns: minmax(120px, 1fr) 44px;
  align-items: center;
  gap: 10px;
  padding-top: 4px;
}
.dash-tone--over {
  color: var(--over);
}
.dash-tone--watch {
  color: var(--hivis-text);
}
.dash-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
}
.dash-status--ok {
  color: var(--ok);
}
.dash-status--ok:hover {
  text-decoration: underline;
}
.dash-status--bad {
  color: var(--over);
}
.dash-empty {
  color: var(--muted);
  font-size: 15px;
  margin: 24px 0;
}

/* yard activity */
.dash-feeds {
  display: grid;
  grid-template-columns: 1fr;
}
.dash-feed {
  min-width: 0;
  padding: 18px 20px 14px;
}
.dash-feed + .dash-feed {
  border-top: 1px solid var(--line);
}
@media (min-width: 960px) {
  .dash-feeds {
    grid-template-columns: repeat(3, 1fr);
  }
  .dash-feed + .dash-feed {
    border-top: 0;
    border-left: 1px solid var(--line);
  }
}
.dash-feed__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 6px;
}
.dash-live {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--muted);
}
.dash-live__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #2e9e4f;
}
.dash-feed__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0 !important;
  min-height: 0 !important;
  border-bottom: 1px solid var(--line);
}
.dash-feed__row:last-child {
  border-bottom: 0;
}
/* v-list-item's ::after only sets a min-height; as a flex child it adds a
   trailing gap that keeps the side column off the right edge. */
.dash-feed__row::after {
  display: none;
}
/* Take up the free space, so the side column sits flush right. Relying on
   space-between alone fails because v-list-item adds an ::after flex child. */
.dash-feed__main {
  flex: 1 1 auto;
  min-width: 0;
}
.dash-feed__street {
  font-weight: 600;
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dash-feed__side {
  text-align: right;
  flex-shrink: 0;
}
.dash-empty--feed {
  margin: 12px 0;
}
.dash-pill {
  display: inline-block;
  margin-top: 3px;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}
.dash-pill--watch {
  background: #fdeedd;
  color: var(--hivis-text);
}
.dash-pill--over {
  background: #fbe4e4;
  color: var(--over);
}
.dash-pill--quiet {
  background: #eef1f5;
  color: var(--muted);
}

@media (max-width: 600px) {
  .dash-headline {
    font-size: 32px;
  }
  .dash-search {
    max-width: none;
    width: 100%;
  }
}

@media print {
  .dash-noprint {
    display: none !important;
  }
  .dash-print {
    border: 0 !important;
  }
}
</style>
